import { ReactNode } from 'react';

import { Dimensions, Metrics } from 'components/insights/constants';
import {
  formatDaysValue,
  formatDecimal,
  formatPercentage,
  formatResponseTime,
  formatWholeNumber,
} from 'components/insights/formatting';
import { NetworkPerformanceMetricDrillKey } from 'stores/networkPerformanceStore';

import { METRIC_CONFIGS, NetworkPerformanceMetrics } from './constants';

// --- Per-metric column definition ---

export type MetricColumn = {
  key: string;
  header: string;
  metric: Metrics;
  format: (v: number | null) => string;
  renderCell?: (values: Record<string, number | null>) => ReactNode;
  pivotDimension?: string;
  pivotValue?: string;
  width?: number;
};

export const formatCountWithPct = (count: number, pct: number): ReactNode => (
  <span>
    {count} <span style={{ opacity: 0.5 }}>({pct}%)</span>
  </span>
);

// --- Breakdown column configs ---

export type BreakdownColumnConfig = {
  columns: MetricColumn[];
  defaultSortId: string;
  defaultSortDesc: boolean;
};

const M = NetworkPerformanceMetrics;

export const getBreakdownColumnConfigs = (
  groupTypeName: string
): Record<NetworkPerformanceMetricDrillKey, BreakdownColumnConfig> => ({
  [M.AVG_START_OF_CARE]: {
    columns: [
      {
        key: 'avgStartOfCare',
        header: METRIC_CONFIGS[M.AVG_START_OF_CARE].primaryMetricLabel,
        metric: Metrics.DAYS_TO_START_CARE_AVERAGE,
        format: formatDaysValue,
      },
      {
        key: 'patientVolume',
        header: 'Patient Volume',
        metric: Metrics.LOCATION_EPISODE_ID_COUNT,
        format: formatWholeNumber,
      },
    ],
    defaultSortId: 'avgStartOfCare',
    defaultSortDesc: true,
  },
  [M.SERVICE_REFUSALS]: {
    columns: [
      {
        key: 'totalRefusals',
        header: METRIC_CONFIGS[M.SERVICE_REFUSALS].primaryMetricLabel,
        metric: Metrics.SERVICE_REFUSALS_COUNT,
        format: formatWholeNumber,
      },
      {
        key: 'refusedByPatient',
        header: 'Refused by Patient',
        metric: Metrics.SERVICE_REFUSALS_COUNT,
        format: formatWholeNumber,
        pivotDimension: Dimensions.SERVICE_REFUSAL_REFUSED_BY_TYPE,
        pivotValue: 'patient',
        renderCell: (values) => {
          const count = values.refusedByPatient ?? 0;
          const total = values.totalRefusals;
          const pct = total ? Math.round((count / total) * 100) : 0;
          return formatCountWithPct(count, pct);
        },
      },
      {
        key: 'refusedByFacility',
        header: `Refused by ${groupTypeName}`,
        metric: Metrics.SERVICE_REFUSALS_COUNT,
        format: formatWholeNumber,
        pivotDimension: Dimensions.SERVICE_REFUSAL_REFUSED_BY_TYPE,
        pivotValue: 'rehab_facility',
        renderCell: (values) => {
          const count = values.refusedByFacility ?? 0;
          const total = values.totalRefusals;
          const pct = total ? Math.round((count / total) * 100) : 0;
          return formatCountWithPct(count, pct);
        },
      },
    ],
    defaultSortId: 'totalRefusals',
    defaultSortDesc: true,
  },
  [M.SAFE_RETURNS]: {
    columns: [
      {
        key: 'safeReturns',
        header: METRIC_CONFIGS[M.SAFE_RETURNS].primaryMetricLabel,
        metric: Metrics.SAFE_RETURNS_REQUESTED_COUNT,
        format: formatWholeNumber,
      },
      {
        key: 'avgResponseTime',
        header: 'Avg. Safe Return Response Time',
        metric: Metrics.SAFE_RETURN_RESPONSE_TIME_AVERAGE,
        format: (v) => formatResponseTime(v),
      },
    ],
    defaultSortId: 'safeReturns',
    defaultSortDesc: true,
  },
  [M.OES]: {
    columns: [
      {
        key: 'oes',
        header: METRIC_CONFIGS[M.OES].primaryMetricLabel,
        metric: Metrics.OLIO_ENGAGEMENT_SCORE,
        format: (v) => formatPercentage(v, 0),
        width: 220,
      },
      {
        key: 'patientVolume',
        header: 'Total Discharges',
        metric: Metrics.ID_COUNT,
        format: formatWholeNumber,
        width: 180,
      },
      {
        key: 'avgLos',
        header: 'Avg. Length of Service',
        metric: Metrics.LENGTH_OF_STAY_AVERAGE,
        format: formatDaysValue,
      },
    ],
    defaultSortId: 'oes',
    defaultSortDesc: true,
  },
  [M.READMISSION_RATE]: {
    columns: [
      {
        key: 'readmissionRate',
        header: METRIC_CONFIGS[M.READMISSION_RATE].primaryMetricLabel,
        metric: Metrics.READMISSION_RATE,
        format: formatPercentage,
      },
      { key: 'totalDischarges', header: 'Total Discharges', metric: Metrics.ID_COUNT, format: formatWholeNumber },
      {
        key: 'readmissionCount',
        header: 'Readmissions',
        metric: Metrics.READMISSIONS_SUM,
        format: formatWholeNumber,
      },
    ],
    defaultSortId: 'readmissionRate',
    defaultSortDesc: true,
  },
  [M.ENGAGEMENTS]: {
    columns: [
      {
        key: 'eppw',
        header: METRIC_CONFIGS[M.ENGAGEMENTS].primaryMetricLabel,
        metric: Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK,
        format: formatDecimal,
        width: 250,
      },
      {
        key: 'totalEngagements',
        header: 'Total Engagements',
        metric: Metrics.OWNER_ACTIVITIES_SUM,
        format: formatWholeNumber,
        width: 180,
      },
      {
        key: 'totalPatients',
        header: 'Total Patients',
        metric: Metrics.ID_COUNT,
        format: formatWholeNumber,
        width: 180,
      },
    ],
    defaultSortId: 'eppw',
    defaultSortDesc: true,
  },
});
